import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, X } from "lucide-react";
import { getHelpForRoute, helpContent, type HelpEntry } from "@/lib/helpContent";
import { useTextMode } from "@/hooks/useTextMode";
import { cn } from "@/lib/utils";

export default function HelpDrawer() {
  const [open, setOpen] = useState(false);
  const [overrideEntry, setOverrideEntry] = useState<HelpEntry | null>(null);
  const location = useLocation();
  const routeHelp = getHelpForRoute(location.pathname);
  const help = overrideEntry || routeHelp;
  const { t } = useTextMode();

  const handleGuideEvent = useCallback((e: Event) => {
    const key = (e as CustomEvent).detail as string;
    const entry = helpContent[key];
    if (entry) {
      setOverrideEntry(entry);
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("open-help-guide", handleGuideEvent);
    return () => window.removeEventListener("open-help-guide", handleGuideEvent);
  }, [handleGuideEvent]);

  const handleClose = () => {
    setOpen(false);
    setOverrideEntry(null);
  };

  if (!help && !open) return null;

  return (
    <>
      {routeHelp && (
        <button
          onClick={() => { setOverrideEntry(null); setOpen(true); }}
          className={cn(
            "flex items-center justify-center rounded-full",
            "h-8 w-8",
            "bg-card/80 border border-border",
            "hover:bg-primary/15",
            "transition-all duration-300",
          )}
          aria-label="Aide contextuelle"
          title="Aide"
        >
          <HelpCircle className="h-4 w-4 text-primary/70 hover:text-primary transition-colors" />
        </button>
      )}

      {open && help && (
        <div className="fixed inset-0 z-[60] flex justify-end" onClick={handleClose}>
          <div
            className="w-full max-w-sm bg-background border-l border-border shadow-2xl h-full overflow-y-auto animate-in slide-in-from-right-full duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 space-y-5">
              <div className="flex items-start justify-between">
                <h2 className="text-base font-semibold text-foreground">{help.title}</h2>
                <button
                  onClick={handleClose}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <section className="space-y-1.5">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">📌 {t("help.section.purpose")}</h3>
                <p className="text-sm text-foreground leading-relaxed">{help.purpose}</p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">🛠️ {t("help.section.howto")}</h3>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{help.howTo}</p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">⏰ {t("help.section.when")}</h3>
                <p className="text-sm text-foreground leading-relaxed">{help.whenToUse}</p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">⚡ {t("help.section.tips")}</h3>
                <p className="text-sm text-foreground leading-relaxed">{help.tips}</p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">💡 {t("help.section.example")}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed italic">{help.example}</p>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
}